import InsightsService from '@/services/InsightsService'
import moment from 'moment-timezone'
import { isEqual } from 'lodash-es'

export default {
  namespaced: true,
  state: {
    categories: [],
    classifications: [],
    feedbackCount: 0,
    totalFeedbackCount: 0,
    negativeClassifications: [],
    companyWideNegativeClassifications: [],
    positiveClassificationsByCategory: {},
    negativeClassificationsByCategory: {},
    totalMentions: [],
    overallMentionsByDaypart: [],
    filters: {},
    feedbackCountBreakdown: {},
    topDrivers: [],
    summaries: {},
    lastSummaryURL: '/insights/summary' // only needed for iframe
  },
  getters: {
    selectLastSummaryURL: (state) => state.lastSummaryURL,
    selectClassifications(state) {
      return state.classifications
    },
    selectPositiveClassificationsByCategory(state) {
      return state.positiveClassificationsByCategory
    },
    selectNegativeClassificationsByCategory(state) {
      return state.negativeClassificationsByCategory
    },
    selectFilters(state) {
      return state.filters
    },
    selectNonOtherCategories(state) {
      return state.categories.filter((c) => c.name !== 'Other')
    },
    selectOtherCategoryIds(state) {
      return state.categories.filter((c) => c.name === 'Other').map((c) => c._id)
    },
    selectParentCategoryIds(_, getters) {
      return getters.selectNonOtherCategories.reduce((acc, curr) => {
        if (!curr.path) acc.push(curr._id)
        return acc
      }, [])
    },
    selectCategoryParentMap(_, getters) {
      // TODO add support for paths multiple layers deep
      return getters.selectNonOtherCategories.reduce((acc, curr) => {
        if (!curr.path) {
          acc[curr._id] = null
        } else {
          acc[curr._id] = curr.path.split('/').pop()
        }
        return acc
      }, {})
    },
    selectCategoryNames(_, getters) {
      return getters.selectNonOtherCategories.reduce(
        (acc, curr) => {
          if (!curr.path) {
            acc.push(curr.name)
          }
          return acc
        },
        ['Overall']
      )
    },
    selectHeatmapCategories(_, getters) {
      return getters.selectNonOtherCategories.reduce(
        (acc, curr) => {
          if (!curr.path) acc.push(curr._id)
          return acc
        },
        ['Feedback']
      )
    },
    selectCategory: (state) => (id) => {
      return state.categories.find((el) => el._id === id)
    },
    selectCategoryName: (_, getters) => (id) => {
      return ['Overall', 'Feedback'].includes(id)
        ? id
        : getters.selectNonOtherCategories.find((el) => el._id === id)?.name
    },
    selectTopStrengths(_, getters) {
      const allPcs = getters.selectPositiveClassificationsByCategory
      const pcs = Object.fromEntries(
        Object.entries(allPcs).filter(([key]) => !getters.selectParentCategoryIds.includes(key))
      )
      let sorted = Object.keys(pcs).sort((a, b) => {
        return (
          pcs[b].classifications.length - pcs[a].classifications.length ||
          pcs[a].name.localeCompare(pcs[b].name)
        )
      })
      return sorted.slice(0, 3).map((id) => {
        const category = getters.selectCategory(id)
        if (category.path === '') {
          return { id: category._id, name: category.name, mentions: pcs[id].classifications.length }
        } else {
          const parentCategory = getters.selectCategory(category.path.split('/').pop())
          return {
            id: category._id,
            name: `${parentCategory.name}: ${category.name}`,
            mentions: pcs[id].classifications.length
          }
        }
      })
    },
    selectTopIssues(_, getters) {
      const allNcs = getters.selectNegativeClassificationsByCategory
      const ncs = Object.fromEntries(
        Object.entries(allNcs).filter(([key]) => !getters.selectParentCategoryIds.includes(key))
      )
      let sorted = Object.keys(ncs).sort((a, b) => {
        return (
          ncs[b].classifications.length - ncs[a].classifications.length ||
          ncs[a].name.localeCompare(ncs[b].name)
        )
      })
      return sorted.slice(0, 3).map((id) => {
        const category = getters.selectCategory(id)
        if (category.path === '') {
          return { id: category._id, name: category.name, mentions: ncs[id].classifications.length }
        } else {
          const parentCategory = getters.selectCategory(category.path.split('/').pop())
          return {
            id: category._id,
            name: `${parentCategory.name}: ${category.name}`,
            mentions: ncs[id].classifications.length
          }
        }
      })
    },
    selectTotalMentions(state) {
      return state.totalMentions
    },
    selectIsLocationFiltered(state) {
      return !!state.filters?.locations?.length
    },
    /**
     * If dayNumber is undefined it returns overall, else dynamically calculates by the dayNumber
     * @param {*} state
     * @returns
     */
    selectMentionsByDaypart: (state, getters, rootState, rootGetters) => (dayNumber) => {
      if (!dayNumber && dayNumber !== 0) {
        return state.overallMentionsByDaypart
      }

      let negativeDps = { morning: 0, afternoon: 0, evening: 0 }
      let positiveDps = { morning: 0, afternoon: 0, evening: 0 }
      const userDayparts = rootGetters['user/selectDayparts']
      const eveningStart = moment(userDayparts.evening.start, 'h:mm a').subtract(1, 'second')
      const morningEnd = moment(userDayparts.morning.end, 'h:mm a').add(1, 'minute')

      state.classifications.forEach((c) => {
        if (c.survey && moment(c.feedbackTextCreated).tz(c.locationTimezone).day() === dayNumber) {
          const localDateTime = moment(c.feedbackTextCreated).tz(c.locationTimezone)
          const comparisonTime = moment()
            .hour(localDateTime.hour())
            .minute(localDateTime.minute())
            .second(localDateTime.second())

          const daypart = comparisonTime.isAfter(eveningStart)
            ? 'evening'
            : comparisonTime.isBefore(morningEnd)
            ? 'morning'
            : 'afternoon'
          c.sentiment === 'NEGATIVE' ? (negativeDps[daypart] += 1) : (positiveDps[daypart] += 1)
        }
      })

      const totalNegative = negativeDps.morning + negativeDps.afternoon + negativeDps.evening
      const totalPositive = positiveDps.morning + positiveDps.afternoon + positiveDps.evening

      if (!totalNegative && !totalPositive) return

      return [
        {
          name: 'Negative',
          data: Object.keys(negativeDps).map(
            (dp) => (negativeDps[dp] / totalNegative).toFixed(2) * 100 || 0
          )
        },
        {
          name: 'Positive',
          data: Object.keys(positiveDps).map(
            (dp) => (positiveDps[dp] / totalPositive).toFixed(2) * 100 || 0
          )
        }
      ]
    },
    selectHeatmapSeries(state, getters, rootState, rootGetters) {
      // Get filtered classifications for selected location(s)
      const negativeClassifications = state.negativeClassifications

      // Get company-wide classifications (unfiltered by location)
      const companyWideNegativeClassifications = state.companyWideNegativeClassifications

      // Get locations based on current filters
      let locations = state.filters.locations?.length
        ? rootGetters['location/selectFilteredLocations'](state.filters.locations)
        : state.filters.companies?.length
        ? rootGetters['location/selectInsightsLocationsByCompanies'](state.filters.companies)
        : rootGetters['location/selectInsightsLocations']

      // Get ALL locations for company-wide calculations, regardless of filters
      const allCompanyLocations = rootGetters['location/selectInsightsLocations']

      const categories = getters.selectHeatmapCategories
      const parentMap = getters.selectCategoryParentMap
      const categoryIndexMap = categories.reduce((acc, cur, index) => {
        acc[cur] = index
        return acc
      }, {})

      // Remove hidden locations and sort
      locations = locations.filter((l) => !l.hideFromReports)
      const allCompanyLocationsFiltered = allCompanyLocations.filter((l) => !l.hideFromReports)

      locations.sort((a, b) => {
        const locationA = a.friendlyName || a.city
        const locationB = b.friendlyName || b.city
        return locationA > locationB ? -1 : locationB > locationA ? 1 : 0
      })

      const locationIds = locations.map((l) => l._id)
      const allCompanyLocationIds = allCompanyLocationsFiltered.map((l) => l._id)

      // For filtered locations
      let countsByLocation = {}
      for (let location of locations) {
        countsByLocation[location._id] = new Array(categories.length).fill(0)
      }

      // For company-wide (all locations)
      let companyWideCounts = new Array(categories.length).fill(0)

      // Count data for filtered locations
      negativeClassifications.forEach((nc) => {
        nc.locations.forEach((ncl) => {
          if (locationIds.includes(ncl)) {
            countsByLocation[ncl][categoryIndexMap[parentMap[nc.category] || nc.category]] += 1
          }
        })
      })

      // Count data for truly company-wide view using companyWideNegativeClassifications
      companyWideNegativeClassifications.forEach((nc) => {
        nc.locations.forEach((ncl) => {
          if (allCompanyLocationIds.includes(ncl)) {
            companyWideCounts[categoryIndexMap[parentMap[nc.category] || nc.category]] += 1
          }
        })
      })

      // Calculate for filtered/selected locations
      const selected = {
        name: 'Selected Locations',
        data: Object.entries(countsByLocation)
          .reduce((acc, curr) => {
            curr[1].forEach((val, index) => {
              acc[index] += val
            })
            return acc
          }, new Array(categories.length).fill(0))
          .map((d, index) => {
            // Skip calculation for Feedback column
            if (index === 0 && categories[0] === 'Feedback') return -1

            if (state.feedbackCount) {
              const percentage = Math.ceil((d / state.feedbackCount) * 100)
              return percentage <= 100 ? percentage : 100
            } else {
              return 0
            }
          }),
        counts: Object.entries(countsByLocation).reduce((acc, curr) => {
          curr[1].forEach((val, index) => {
            acc[index] += val
          })
          return acc
        }, new Array(categories.length).fill(0)),
        totalCount: state.feedbackCount
      }

      // Calculate for all locations (unfiltered)
      const allLocations = {
        name: 'All Locations',
        data: Object.entries(countsByLocation)
          .reduce((acc, curr) => {
            curr[1].forEach((val, index) => {
              acc[index] += val
            })
            return acc
          }, new Array(categories.length).fill(0))
          .map((d, index) => {
            // Skip calculation for Feedback column
            if (index === 0 && categories[0] === 'Feedback') return -1

            if (state.feedbackCount) {
              const percentage = Math.ceil((d / state.feedbackCount) * 100)
              return percentage <= 100 ? percentage : 100
            } else {
              return 0
            }
          }),
        counts: Object.entries(countsByLocation).reduce((acc, curr) => {
          curr[1].forEach((val, index) => {
            acc[index] += val
          })
          return acc
        }, new Array(categories.length).fill(0)),
        totalCount: state.feedbackCount
      }

      // Calculate total company feedback count
      const totalCompanyFeedbackCount = state.totalFeedbackCount || 0

      // Prepare company-wide data using truly company-wide data
      const companyWideOverall = {
        name: 'Company-wide',
        data: companyWideCounts.map((d, index) => {
          // Skip calculation for Feedback column
          if (index === 0 && categories[0] === 'Feedback') return -1

          if (totalCompanyFeedbackCount) {
            const percentage = Math.ceil((d / totalCompanyFeedbackCount) * 100)
            return percentage <= 100 ? percentage : 100
          } else {
            return 0
          }
        }),
        counts: companyWideCounts,
        totalCount: totalCompanyFeedbackCount
      }

      // Format individual location data
      const locationSeries = Object.entries(countsByLocation).reduce((acc, curr) => {
        const locationFeedbackCount = getters.selectLocationFeedbackCount(curr[0])
        acc.push({
          name: curr[0],
          data: curr[1].map((d, index) => {
            // Skip calculation for Feedback column
            if (index === 0 && categories[0] === 'Feedback') return -1

            if (locationFeedbackCount) {
              const percentage = Math.ceil((d / locationFeedbackCount) * 100)
              return percentage <= 100 ? percentage : 100
            } else {
              return 0
            }
          }),
          counts: curr[1],
          totalCount: locationFeedbackCount
        })
        return acc
      }, [])

      // Build the final data series with appropriate ordering based on filter context
      let returnData = [...locationSeries]

      if (locations.length > 1) {
        if (getters.selectIsLocationFiltered) {
          // When locations are filtered, show Selected Locations and Company-wide
          // but NOT All Locations (which would be redundant)
          returnData.push(selected)
        } else {
          // When no location filtering, show only All Locations
          returnData.push(allLocations)
        }
      }

      // Always show Company-wide when locations are filtered
      if (getters.selectIsLocationFiltered) {
        returnData.push(companyWideOverall)
      }

      // -1 means grab count instead of percentage in Heatmap for first column
      if (categories[0] === 'Feedback') {
        returnData.forEach((series) => {
          if (series.data[0] !== -1) {
            series.data[0] = -1
          }
        })
      }

      return returnData
    },
    selectFeedbackCount(state) {
      return state.feedbackCount
    },
    selectSubcategories: (state, getters) => (id) => {
      return getters.selectNonOtherCategories.filter((c) => new RegExp(id, 'i').test(c.path))
    },
    selectLocationFeedbackCount: (state) => (id) => {
      const counts = state.feedbackCountBreakdown[id]
      return counts ? counts.reviewCount + counts.surveyCount : 0
    },
    selectTopDrivers(state) {
      return state.topDrivers
    },
    selectSummaries: (state) => state.summaries
  },
  mutations: {
    SET_CLASSIFICATIONS(state, classifications) {
      state.classifications = classifications
    },
    SET_NEGATIVE_CLASSIFICATIONS(state, negativeClassifications) {
      state.negativeClassifications = negativeClassifications
    },
    SET_COMPANY_WIDE_NEGATIVE_CLASSIFICATIONS(state, negativeClassifications) {
      state.companyWideNegativeClassifications = negativeClassifications
    },
    SET_POSITIVE_CLASSIFICATIONS_BY_CATEGORY(state, positiveClassificationsByCategory) {
      state.positiveClassificationsByCategory = positiveClassificationsByCategory
    },
    SET_NEGATIVE_CLASSIFICATIONS_BY_CATEGORY(state, negativeClassificationsByCategory) {
      state.negativeClassificationsByCategory = negativeClassificationsByCategory
    },
    SET_TOTAL_MENTIONS(state, totalMentions) {
      state.totalMentions = totalMentions
    },
    SET_OVERALL_MENTIONS_BY_DAYPART(state, overallMentionsByDaypart) {
      state.overallMentionsByDaypart = overallMentionsByDaypart
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    SET_FILTERS(state, filters) {
      state.filters = filters
    },
    SET_FEEDBACK_COUNT(state, feedbackCount) {
      state.feedbackCount = feedbackCount
    },
    SET_TOTAL_FEEDBACK_COUNT(state, totalFeedbackCount) {
      state.totalFeedbackCount = totalFeedbackCount
    },
    SET_FEEDBACK_COUNT_BREAKDOWN(state, feedbackCountBreakdown) {
      state.feedbackCountBreakdown = feedbackCountBreakdown
    },
    SET_TOP_DRIVERS(state, drivers) {
      state.topDrivers = drivers
    },
    SET_SUMMARIES(state, summaries) {
      state.summaries = summaries
    },
    SET_LAST_SUMMARY_URL(state, url) {
      state.lastSummaryURL = url
    }
  },
  actions: {
    async applyFilters({ commit, getters }, filters) {
      const { selectFilters } = getters
      if (isEqual(filters, selectFilters)) return
      commit('SET_FILTERS', filters)
    },

    async setLastSummaryUrl({ commit }, url) {
      commit('SET_LAST_SUMMARY_URL', url)
    },

    async formatFilters({ getters, rootState, rootGetters }, { allLocations = false } = {}) {
      const { activeCompany } = rootState.company
      const filters = getters.selectFilters
      const payloadFilters = {
        createdAtRange: filters.dateRange || [
          moment().startOf('d').subtract(30, 'd').utc().format(),
          moment().endOf('day').utc().format()
        ]
      }

      // Handle companies filtering
      if (allLocations) {
        // For company-wide view, include all available companies
        payloadFilters.companyIds = rootGetters['company/selectInsightsCompanies'].map((c) => c._id)
      } else if (activeCompany._id === 'ALL_COMPANIES') {
        if (filters.companies && filters.companies.length) {
          payloadFilters.companyIds = Array.isArray(filters.companies)
            ? filters.companies
            : [filters.companies]
        } else {
          payloadFilters.companyIds = rootGetters['company/selectInsightsCompanies'].map(
            (c) => c._id
          )
        }
      } else {
        payloadFilters.companyIds = [activeCompany._id]
      }

      // Handle locations filtering
      if (filters.locations && !allLocations) {
        // Apply location filter only when not requesting all locations
        payloadFilters.locationIds = Array.isArray(filters.locations)
          ? filters.locations
          : [filters.locations]
      } else {
        // Get all locations for company-wide view or when no location filter is applied
        payloadFilters.locationIds = rootGetters['location/selectInsightsLocations']
          .filter((l) => !l.hideFromReports && !l.disabled)
          .map((l) => l._id)
      }

      // Include source filters if present
      if (filters.sources?.length) {
        payloadFilters.sources = filters.sources
      }

      return payloadFilters
    },

    async fetchCategories({ commit }) {
      const response = await InsightsService.fetchCategories()
      commit('SET_CATEGORIES', response.body.data.categories)
    },

    async fetchFeedbackCount({ commit, dispatch, getters }) {
      // Always fetch both counts: filtered and company-wide
      const [filteredResponse, totalResponse] = await Promise.all([
        // Get feedback count for current filters
        InsightsService.fetchFeedbackCount({
          filters: await dispatch('formatFilters', { allLocations: false })
        }),
        // Get feedback count for ALL locations
        InsightsService.fetchFeedbackCount({
          filters: await dispatch('formatFilters', { allLocations: true })
        })
      ])

      commit('SET_FEEDBACK_COUNT', filteredResponse.body.data)
      commit('SET_TOTAL_FEEDBACK_COUNT', totalResponse.body.data)
    },

    async fetchFeedbackCountBreakdown({ commit, dispatch }) {
      const response = await InsightsService.fetchFeedbackCountBreakdown({
        filters: await dispatch('formatFilters')
      })
      commit('SET_FEEDBACK_COUNT_BREAKDOWN', response.body.data)
    },

    async fetchClassifications({ dispatch, getters }) {
      const filters = getters.selectFilters

      // First, fetch filtered classifications based on current filters
      let skip = 0
      const limit = 12000
      let hasMoreData = true
      let filteredClassifications = []
      let safetyStop = 0

      while (hasMoreData && safetyStop < 20) {
        const response = await InsightsService.fetchClassifications({
          filters: await dispatch('formatFilters', { allLocations: false }),
          ...(filters.keywords?.length ? { search: filters.keywords } : {}),
          limit,
          skip
        })

        const classifications = response.body.data
        if (classifications.length === limit) {
          skip += limit
        } else {
          hasMoreData = false
        }
        filteredClassifications = filteredClassifications.concat(classifications)
        safetyStop++
      }

      // Process filtered classifications
      await dispatch(
        'processClassifications',
        getters.selectIsLocationFiltered
          ? filteredClassifications.filter((c) => {
              if (c.location) {
                return filters.locations?.includes(c.location)
              } else {
                return !!filters.locations?.filter((l) => c.locations?.includes(l)).length
              }
            })
          : filteredClassifications
      )

      // Now separately fetch ALL classifications for company-wide view
      // Reset counters for second fetch
      skip = 0
      hasMoreData = true
      let allCompanyClassifications = []
      safetyStop = 0

      while (hasMoreData && safetyStop < 20) {
        const response = await InsightsService.fetchClassifications({
          // Use formatFilters with allLocations=true to get all locations
          filters: await dispatch('formatFilters', { allLocations: true }),
          ...(filters.keywords?.length ? { search: filters.keywords } : {}),
          limit,
          skip
        })

        const classifications = response.body.data
        if (classifications.length === limit) {
          skip += limit
        } else {
          hasMoreData = false
        }
        allCompanyClassifications = allCompanyClassifications.concat(classifications)
        safetyStop++
      }

      // Process ALL classifications for company-wide view
      await dispatch('processAllClassifications', allCompanyClassifications)
    },

    async fetchSummaries({ commit, dispatch, getters }) {
      const filters = getters.selectFilters
      const response = await InsightsService.fetchSummaries({
        filters: await dispatch('formatFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {})
      })
      const summaries = response.body.data
      commit('SET_SUMMARIES', summaries)
    },

    // this processes all classifications specifically for the 'company-wide' view
    // unfiltered by location. i.e. on the Heatmap
    async processAllClassifications({ commit, getters }, classifications) {
      const negativeClassifications = classifications.filter((c) => c.sentiment === 'NEGATIVE')

      commit('SET_COMPANY_WIDE_NEGATIVE_CLASSIFICATIONS', negativeClassifications)
    },

    // this can process all or a subset if there are locations filtered
    async processClassifications({ commit, getters, rootGetters }, classifications) {
      const categories = getters.selectNonOtherCategories
      // data for splitting
      let positiveClassificationsByCategory = {}
      let negativeClassificationsByCategory = {}
      categories.forEach((cat) => {
        positiveClassificationsByCategory[cat._id] = { name: cat.name, classifications: [] }
        negativeClassificationsByCategory[cat._id] = { name: cat.name, classifications: [] }
      })
      // data for total mentions
      let totalMentions = [
        { name: 'Negative', data: new Array(7).fill(0) },
        { name: 'Positive', data: new Array(7).fill(0) }
      ]
      // data for overall mentions by daypart
      let negativeDps = { morning: 0, afternoon: 0, evening: 0 }
      let positiveDps = { morning: 0, afternoon: 0, evening: 0 }
      const userDayparts = rootGetters['user/selectDayparts']
      const eveningStart = moment(userDayparts.evening.start, 'h:mm a').subtract(1, 'second')
      const morningEnd = moment(userDayparts.morning.end, 'h:mm a').add(1, 'minute')
      // data for heatmap
      let negativeClassifications = []

      // data for mention drivers
      let driverMap = {}
      let topDrivers = []
      for (let i = 0; i < 7; i++) {
        topDrivers.push({
          positive: [],
          negative: []
        })
      }
      categories.forEach((cat) => {
        driverMap[cat._id] = {
          POSITIVE: new Array(7).fill(0),
          NEGATIVE: new Array(7).fill(0)
        }
      })

      // single pass over classifications for parsing
      classifications
        .filter((c) => !getters.selectOtherCategoryIds.includes(c.category))
        .forEach((c) => {
          // split positive/negative processing
          c.sentiment === 'POSITIVE'
            ? positiveClassificationsByCategory[c.category].classifications.push(c)
            : negativeClassificationsByCategory[c.category].classifications.push(c)

          // total mentions processing
          const seriesIndex = c.sentiment === 'NEGATIVE' ? 0 : 1
          if (c.review) {
            totalMentions[seriesIndex].data[moment(c.feedbackTextCreated).utc().day()] += 1
          } else {
            totalMentions[seriesIndex].data[
              moment(c.feedbackTextCreated).tz(c.locationTimezone).day()
            ] += 1
          }

          // drivers processing
          driverMap[c.category][c.sentiment][moment(c.feedbackTextCreated).utc().day()] += 1

          // heatmap processing
          if (c.sentiment === 'NEGATIVE') negativeClassifications.push(c)

          // overall mentions by daypart processing
          if (c.survey) {
            const localDateTime = moment(c.feedbackTextCreated).tz(c.locationTimezone)
            const comparisonTime = moment()
              .hour(localDateTime.hour())
              .minute(localDateTime.minute())
              .second(localDateTime.second())

            const daypart = comparisonTime.isAfter(eveningStart)
              ? 'evening'
              : comparisonTime.isBefore(morningEnd)
              ? 'morning'
              : 'afternoon'
            c.sentiment === 'NEGATIVE' ? (negativeDps[daypart] += 1) : (positiveDps[daypart] += 1)
          }
        })

      // top mention driver processing
      for (const catId in driverMap) {
        const driver = driverMap[catId]
        driver.POSITIVE.forEach((count, index) => {
          topDrivers[index].positive.push({ categoryId: catId, count })
        })
        driver.NEGATIVE.forEach((count, index) => {
          topDrivers[index].negative.push({ categoryId: catId, count })
        })
      }
      topDrivers = topDrivers.map((td) => {
        return {
          positive: td.positive
            .sort((a, b) => {
              return a.count > b.count ? -1 : b.count > a.count ? 1 : 0
            })
            .slice(0, 3),
          negative: td.negative
            .sort((a, b) => {
              return a.count > b.count ? -1 : b.count > a.count ? 1 : 0
            })
            .slice(0, 3)
        }
      })

      // overall mentions by daypart processing
      const totalNegative = negativeDps.morning + negativeDps.afternoon + negativeDps.evening
      const totalPositive = positiveDps.morning + positiveDps.afternoon + positiveDps.evening
      const overallMentionsByDaypart =
        totalNegative || totalPositive
          ? [
              {
                name: 'Negative',
                data: Object.keys(negativeDps).map(
                  (dp) => (negativeDps[dp] / totalNegative).toFixed(2) * 100 || 0
                )
              },
              {
                name: 'Positive',
                data: Object.keys(positiveDps).map(
                  (dp) => (positiveDps[dp] / totalPositive).toFixed(2) * 100 || 0
                )
              }
            ]
          : []

      // check if there is any data in totalMentions, assign empty array to show empty state
      totalMentions =
        totalMentions[0].data.every((i) => i === 0) && totalMentions[1].data.every((i) => i === 0)
          ? []
          : totalMentions

      commit('SET_CLASSIFICATIONS', classifications)
      commit('SET_NEGATIVE_CLASSIFICATIONS', negativeClassifications)
      commit('SET_POSITIVE_CLASSIFICATIONS_BY_CATEGORY', positiveClassificationsByCategory)
      commit('SET_NEGATIVE_CLASSIFICATIONS_BY_CATEGORY', negativeClassificationsByCategory)
      commit('SET_TOTAL_MENTIONS', totalMentions)
      commit('SET_OVERALL_MENTIONS_BY_DAYPART', overallMentionsByDaypart)
      commit('SET_TOP_DRIVERS', topDrivers)
    }
  }
}
